// Web Audio API synthesizer — distinct ambient soundscapes per game state.
// No external audio files needed — everything generated procedurally.

let ctx = null;
let masterGain = null;

export function getCtx() { return ctx; }
export function getMasterGain() { return masterGain; }

// Engine / coaster
let engineOsc, engineGain, engineFilter;
let railRumbleNoise, railRumbleGain, railRumbleFilter;

// Airborne
let windNoise, windGain;

// Swim
let swimNoise, swimGain;
let swimStrokeTimer = null;

// Walking / Running
let lastFootstepAt = 0;
let breathingOsc, breathingGain;

// Ambient birds (only when idle on ground)
let birdsTimer = null;
let birdsActive = false;
let birdsMasterGain = null;

let _noiseBuffer = null;

function ensureCtx() {
  if (ctx) return ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.45;
    masterGain.connect(ctx.destination);
  } catch (e) {
    console.warn("AudioContext not available", e);
  }
  return ctx;
}

export function initAudio() {
  ensureCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
}

function getNoiseBuffer() {
  if (_noiseBuffer || !ctx) return _noiseBuffer;
  const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  _noiseBuffer = buf;
  return buf;
}

// ========== ENGINE (electric cart on rails) ==========
export function setEngine(active, speed = 0) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!engineOsc) {
      engineOsc = ctx.createOscillator();
      engineOsc.type = "sawtooth";
      engineGain = ctx.createGain();
      engineFilter = ctx.createBiquadFilter();
      engineFilter.type = "lowpass";
      engineFilter.frequency.value = 700;
      engineOsc.connect(engineFilter);
      engineFilter.connect(engineGain);
      engineGain.connect(masterGain);
      engineGain.gain.value = 0;
      engineOsc.start();
    }
    const freq = 55 + Math.min(speed, 35) * 5;
    engineOsc.frequency.setTargetAtTime(freq, ctx.currentTime, 0.1);
    engineFilter.frequency.setTargetAtTime(
      450 + Math.min(speed, 35) * 30,
      ctx.currentTime,
      0.15,
    );
    engineGain.gain.setTargetAtTime(0.10 + Math.min(speed, 25) * 0.004, ctx.currentTime, 0.2);
  } else if (engineGain) {
    engineGain.gain.setTargetAtTime(0, ctx.currentTime, 0.15);
  }
}

// ========== RAIL RUMBLE (clackety wheels) ==========
export function setRailRumble(active, speed = 0) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!railRumbleNoise) {
      railRumbleNoise = ctx.createBufferSource();
      railRumbleNoise.buffer = getNoiseBuffer();
      railRumbleNoise.loop = true;
      railRumbleFilter = ctx.createBiquadFilter();
      railRumbleFilter.type = "lowpass";
      railRumbleFilter.frequency.value = 200;
      railRumbleGain = ctx.createGain();
      railRumbleGain.gain.value = 0;
      railRumbleNoise.connect(railRumbleFilter);
      railRumbleFilter.connect(railRumbleGain);
      railRumbleGain.connect(masterGain);
      railRumbleNoise.start();
    }
    railRumbleGain.gain.setTargetAtTime(
      Math.min(0.18, 0.04 + speed * 0.006),
      ctx.currentTime,
      0.2,
    );
    railRumbleFilter.frequency.setTargetAtTime(
      150 + speed * 15,
      ctx.currentTime,
      0.2,
    );
  } else if (railRumbleGain) {
    railRumbleGain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
  }
}

// ========== WIND (airborne) ==========
export function setWind(active, speed = 0) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!windNoise) {
      windNoise = ctx.createBufferSource();
      windNoise.buffer = getNoiseBuffer();
      windNoise.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 700;
      filter.Q.value = 0.6;
      windGain = ctx.createGain();
      windGain.gain.value = 0;
      windNoise.connect(filter);
      filter.connect(windGain);
      windGain.connect(masterGain);
      windNoise.start();
    }
    const target = Math.min(0.4, 0.05 + speed * 0.012);
    windGain.gain.setTargetAtTime(target, ctx.currentTime, 0.2);
  } else if (windGain) {
    windGain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
  }
}

// ========== SWIMMING ==========
export function setSwim(active) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!swimNoise) {
      swimNoise = ctx.createBufferSource();
      swimNoise.buffer = getNoiseBuffer();
      swimNoise.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 350;
      swimGain = ctx.createGain();
      swimGain.gain.value = 0;
      swimNoise.connect(filter);
      filter.connect(swimGain);
      swimGain.connect(masterGain);
      swimNoise.start();
    }
    swimGain.gain.setTargetAtTime(0.18, ctx.currentTime, 0.3);
    // Periodic stroke splashes
    if (!swimStrokeTimer) {
      swimStrokeTimer = setInterval(() => playSwimStroke(), 900);
    }
  } else {
    if (swimGain) swimGain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
    if (swimStrokeTimer) {
      clearInterval(swimStrokeTimer);
      swimStrokeTimer = null;
    }
  }
}

function playSwimStroke() {
  if (!ctx) return;
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(900, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.3);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
  noise.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.5);
}

// ========== WATER SPLASH (one-shot, on entry) ==========
export function playSplash() {
  ensureCtx();
  if (!ctx) return;
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1500;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.7, ctx.currentTime + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
  noise.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.95);
}

// ========== FOOTSTEPS ==========
export function playFootstep(running = false) {
  ensureCtx();
  if (!ctx) return;
  const now = performance.now();
  const interval = running ? 270 : 480;
  if (now - lastFootstepAt < interval) return;
  lastFootstepAt = now;

  // Two noise bursts: low thump + high crunch (grass+dirt)
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = running ? 200 : 140;
  filter.Q.value = 1.2;
  const g = ctx.createGain();
  const peak = running ? 0.45 : 0.28;
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);
  noise.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.16);

  // crunchy higher freq overlay
  const noise2 = ctx.createBufferSource();
  noise2.buffer = getNoiseBuffer();
  const filter2 = ctx.createBiquadFilter();
  filter2.type = "highpass";
  filter2.frequency.value = 1800;
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, ctx.currentTime);
  g2.gain.exponentialRampToValueAtTime(running ? 0.1 : 0.06, ctx.currentTime + 0.005);
  g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
  noise2.connect(filter2);
  filter2.connect(g2);
  g2.connect(masterGain);
  noise2.start();
  noise2.stop(ctx.currentTime + 0.1);
}

// ========== BREATHING (when running) ==========
export function setBreathing(active) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!breathingOsc) {
      // Use a noise-based whisper for breath
      breathingOsc = ctx.createBufferSource();
      breathingOsc.buffer = getNoiseBuffer();
      breathingOsc.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 600;
      filter.Q.value = 1.5;
      // LFO to make it pulsate like breathing
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 1.0; // ~1Hz breathing rate
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.15;
      breathingGain = ctx.createGain();
      breathingGain.gain.value = 0;
      lfo.connect(lfoGain);
      lfoGain.connect(breathingGain.gain);
      lfo.start();
      breathingOsc.connect(filter);
      filter.connect(breathingGain);
      breathingGain.connect(masterGain);
      breathingOsc.start();
    }
    breathingGain.gain.setTargetAtTime(0.18, ctx.currentTime, 0.3);
  } else if (breathingGain) {
    breathingGain.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
  }
}

// ========== BIRDS (when idle on ground) ==========
function playBirdChirp() {
  if (!ctx || !birdsActive) return;
  const t0 = ctx.currentTime;
  const baseFreq = 1800 + Math.random() * 1800;
  const notes = 2 + Math.floor(Math.random() * 3); // 2-4 quick chirps
  for (let i = 0; i < notes; i++) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    const start = t0 + i * 0.12;
    osc.frequency.setValueAtTime(baseFreq * (0.9 + Math.random() * 0.3), start);
    osc.frequency.exponentialRampToValueAtTime(
      baseFreq * (0.7 + Math.random() * 0.3),
      start + 0.08,
    );
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.12, start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.09);
    osc.connect(g);
    if (birdsMasterGain) g.connect(birdsMasterGain);
    osc.start(start);
    osc.stop(start + 0.1);
  }
}

export function setBirds(active) {
  ensureCtx();
  if (!ctx) return;
  if (active && !birdsActive) {
    birdsActive = true;
    if (!birdsMasterGain) {
      birdsMasterGain = ctx.createGain();
      birdsMasterGain.gain.value = 0;
      birdsMasterGain.connect(masterGain);
    }
    birdsMasterGain.gain.setTargetAtTime(0.6, ctx.currentTime, 0.5);
    // Schedule chirps at random intervals 1-4s
    const schedule = () => {
      if (!birdsActive) return;
      playBirdChirp();
      const next = 1000 + Math.random() * 3500;
      birdsTimer = setTimeout(schedule, next);
    };
    schedule();
  } else if (!active && birdsActive) {
    birdsActive = false;
    if (birdsMasterGain)
      birdsMasterGain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
    if (birdsTimer) clearTimeout(birdsTimer);
    birdsTimer = null;
  }
}

// ========== ONE-SHOTS ==========
export function playJump() {
  ensureCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(160, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(420, ctx.currentTime + 0.25);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
  osc.connect(g);
  g.connect(masterGain);
  osc.start();
  osc.stop(ctx.currentTime + 0.32);
}

export function playChute() {
  ensureCtx();
  if (!ctx) return;
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 900;
  filter.Q.value = 2;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.5, ctx.currentTime + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
  noise.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.55);
}

export function playChime() {
  ensureCtx();
  if (!ctx) return;
  [880, 1320].forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = ctx.createGain();
    const t0 = ctx.currentTime + i * 0.08;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
    osc.connect(g);
    g.connect(masterGain);
    osc.start(t0);
    osc.stop(t0 + 0.62);
  });
}

// Climbing ladder sound
export function playClimb() {
  ensureCtx();
  if (!ctx) return;
  // Two clunks rising in pitch
  [200, 280, 360].forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = f;
    const g = ctx.createGain();
    const t0 = ctx.currentTime + i * 0.18;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
    osc.connect(g);
    g.connect(masterGain);
    osc.start(t0);
    osc.stop(t0 + 0.2);
  });
}

// ========== SPEED SIREN ==========
let sirenOsc = null;
let sirenGain = null;
let sirenActive = false;

export function setSiren(active) {
  ensureCtx();
  if (!ctx) return;
  if (active && !sirenActive) {
    sirenActive = true;
    sirenOsc = ctx.createOscillator();
    sirenOsc.type = "sawtooth";
    sirenGain = ctx.createGain();
    sirenGain.gain.value = 0.18;
    sirenOsc.connect(sirenGain);
    sirenGain.connect(masterGain);
    // Wail: oscillate freq between 600 and 900 Hz at ~2Hz
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 2;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 150;
    lfo.connect(lfoGain);
    lfoGain.connect(sirenOsc.frequency);
    sirenOsc.frequency.value = 750;
    lfo.start();
    sirenOsc.start();
  } else if (!active && sirenActive) {
    sirenActive = false;
    if (sirenGain) sirenGain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
    sirenOsc = null;
    sirenGain = null;
  }
}

// ========== CHASE MUSIC (JURASSIC) ==========
let chaseOsc1, chaseOsc2, chaseGain;
let chaseIntensityVal = 0;

export function setChaseMusic(intensity) {
  ensureCtx();
  if (!ctx) return;
  chaseIntensityVal = intensity;
  
  if (intensity > 0 && !chaseGain) {
    chaseGain = ctx.createGain();
    chaseGain.gain.value = 0;
    chaseGain.connect(masterGain);
    
    chaseOsc1 = ctx.createOscillator();
    chaseOsc1.type = "sawtooth";
    chaseOsc2 = ctx.createOscillator();
    chaseOsc2.type = "square";
    
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 400;
    
    chaseOsc1.connect(filter);
    chaseOsc2.connect(filter);
    filter.connect(chaseGain);
    
    chaseOsc1.start();
    chaseOsc2.start();
  }
  
  if (chaseGain) {
    const targetVolume = intensity * 0.3;
    chaseGain.gain.setTargetAtTime(targetVolume, ctx.currentTime, 1.0);
    
    if (chaseOsc1 && chaseOsc2) {
      chaseOsc1.frequency.setTargetAtTime(60 + intensity * 40, ctx.currentTime, 0.5);
      chaseOsc2.frequency.setTargetAtTime(65 + intensity * 42, ctx.currentTime, 0.5);
    }
  }
  
  if (intensity === 0 && chaseGain) {
    setTimeout(() => {
      if (chaseIntensityVal === 0) {
        chaseOsc1?.stop();
        chaseOsc2?.stop();
        chaseGain = null;
      }
    }, 1500);
  }
}

// ========== PHASE 2: MAGLEV HUM (continuous, pitch tracks speed) ==========
let maglevOsc1, maglevOsc2, maglevGain, maglevFilter;

export function setMaglevHum(active, speed = 0) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!maglevOsc1) {
      // Layered sine + triangle for smooth electric hum
      maglevOsc1 = ctx.createOscillator();
      maglevOsc1.type = "sine";
      maglevOsc2 = ctx.createOscillator();
      maglevOsc2.type = "triangle";
      maglevFilter = ctx.createBiquadFilter();
      maglevFilter.type = "bandpass";
      maglevFilter.Q.value = 3.0;
      maglevGain = ctx.createGain();
      maglevGain.gain.value = 0;
      maglevOsc1.connect(maglevFilter);
      maglevOsc2.connect(maglevFilter);
      maglevFilter.connect(maglevGain);
      maglevGain.connect(masterGain);
      maglevOsc1.start();
      maglevOsc2.start();
    }
    // Pitch rises with speed: 80 Hz at rest → 280 Hz at 40 m/s
    const base = 80 + Math.min(speed, 45) * 4.4;
    maglevOsc1.frequency.setTargetAtTime(base, ctx.currentTime, 0.12);
    maglevOsc2.frequency.setTargetAtTime(base * 1.01, ctx.currentTime, 0.12); // slight beating
    maglevFilter.frequency.setTargetAtTime(base * 2.5, ctx.currentTime, 0.15);
    const targetGain = speed < 1 ? 0.04 : Math.min(0.22, 0.06 + speed * 0.004);
    maglevGain.gain.setTargetAtTime(targetGain, ctx.currentTime, 0.2);
  } else if (maglevGain) {
    maglevGain.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
  }
}

// ========== PHASE 2: WHOOSH (one-shot on high acceleration) ==========
export function playWhoosh() {
  ensureCtx();
  if (!ctx) return;
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(400, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.08);
  filter.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.5);
  filter.Q.value = 1.2;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.45, ctx.currentTime + 0.06);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
  noise.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.6);
}

// ========== PHASE 2: THUNK (braking impact) ==========
export function playThunk() {
  ensureCtx();
  if (!ctx) return;
  // Deep low thump
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(80, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.35);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
  osc.connect(g);
  g.connect(masterGain);
  osc.start();
  osc.stop(ctx.currentTime + 0.45);
  // Noise layer
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer();
  const nf = ctx.createBiquadFilter();
  nf.type = "lowpass";
  nf.frequency.value = 250;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.0001, ctx.currentTime);
  ng.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.01);
  ng.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
  noise.connect(nf);
  nf.connect(ng);
  ng.connect(masterGain);
  noise.start();
  noise.stop(ctx.currentTime + 0.35);
}

// ========== PHASE 2: BUZZER (wrong answer) ==========
export function playBuzzer() {
  ensureCtx();
  if (!ctx) return;
  // Short harsh buzz
  [0, 0.15, 0.30].forEach((delay) => {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = 180;
    const g = ctx.createGain();
    const t0 = ctx.currentTime + delay;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    osc.connect(g);
    g.connect(masterGain);
    osc.start(t0);
    osc.stop(t0 + 0.14);
  });
}

// ========== PHASE 2: FOCUS PAD (calm ambient during questions) ==========
let focusPadOsc1, focusPadOsc2, focusPadOsc3, focusPadGain;

export function setFocusPad(active) {
  ensureCtx();
  if (!ctx) return;
  if (active) {
    if (!focusPadGain) {
      // Soft chord: root + minor third + fifth (calm, contemplative)
      const freqs = [220, 261.6, 329.6]; // A3, C4, E4 — A minor triad
      focusPadGain = ctx.createGain();
      focusPadGain.gain.value = 0;
      const convGain = ctx.createGain();
      convGain.gain.value = 0.6;
      focusPadGain.connect(convGain);
      convGain.connect(masterGain);
      [focusPadOsc1, focusPadOsc2, focusPadOsc3] = freqs.map((f) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = f;
        // LFO for gentle tremolo
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 0.35 + Math.random() * 0.15;
        const lfoG = ctx.createGain();
        lfoG.gain.value = f * 0.004;
        lfo.connect(lfoG);
        lfoG.connect(osc.frequency);
        lfo.start();
        osc.connect(focusPadGain);
        osc.start();
        return osc;
      });
    }
    focusPadGain.gain.setTargetAtTime(0.12, ctx.currentTime, 1.0);
  } else if (focusPadGain) {
    focusPadGain.gain.setTargetAtTime(0, ctx.currentTime, 0.8);
  }
}

// ========== STOP ALL ==========
export function stopAll() {
  setEngine(false);
  setRailRumble(false);
  setWind(false);
  setSwim(false);
  setBreathing(false);
  setBirds(false);
  setSiren(false);
  setChaseMusic(0);
  setMaglevHum(false);
  setFocusPad(false);
}
